import isEmpty from 'lodash/isEmpty'
import { v4 as uuidv4 } from 'uuid'

import {
  ChatExplainRequest,
  ChatHistoryRequest,
  ChatHistoryResponse,
  ChatHistoryResponseItem,
  ChatMessage,
  ChatRequestContext,
  ChatUsersRequest,
  ChatStopRequest,
  ChatQueryRequest,
  ChatDefaultsRequest,
  ChatSettings,
  ChatQueryResponse,
  ChatExplainResponse,
  ChatTextReference,
} from '@dc-shared-web/providers/ChatProvider/types'

export const explainRequest = (
  context: ChatRequestContext,
  text: string,
  references: ChatTextReference[]
): ChatExplainRequest => ({
  command: 'explain',
  context,
  payload: {
    text,
    references,
  },
})

export const historyRequest = (
  context: ChatRequestContext,
  options?: { limit?: number; offset?: number }
): ChatHistoryRequest => ({
  command: 'history',
  context,
  payload: {
    ...(options?.limit !== undefined && { limit: options.limit }),
    ...(options?.offset !== undefined && { offset: options.offset }),
  },
})

export const chatUsersRequest = (
  context: ChatRequestContext
): ChatUsersRequest => ({
  command: 'chat_users',
  context,
  payload: {},
})

export const stopRequest = (context: ChatRequestContext): ChatStopRequest => ({
  command: 'stop',
  context,
  payload: {},
})

export const queryRequest = (
  context: ChatRequestContext,
  text: string,
  uuid: string,
  settings?: ChatSettings
): ChatQueryRequest => ({
  command: 'query',
  context,
  payload: {
    text,
    doc_guid: uuid,
    settings,
  },
})

export const defaultsRequest = (
  context: ChatRequestContext
): ChatDefaultsRequest => ({
  command: 'defaults',
  context,
  payload: {
    settings: {},
  },
})

const formatMessagesFromHistoryItems = (items: ChatHistoryResponseItem[]) => {
  return items.reduce((acc, item) => {
    if (item.human && item.command !== 'explain') {
      acc.push({
        renderId: uuidv4(),
        chat_item_id: `${item.chat_item_id}-human`,
        type: 'human',
        text: item.human.chat_text,
        settings: {},
      })
    }
    if (item.robot && item.robot.chat_text) {
      acc.push({
        renderId: uuidv4(),
        chat_item_id: item.chat_item_id,
        type: item.command === 'explain' ? 'explain' : 'robot',
        text: item.robot.chat_text,
        settings: item.robot.data,
        reference_text: item.reference_text,
        references: item.references,
        completed: true,
        citations:
          isEmpty(item.citations) || !item.citations
            ? undefined
            : item.citations?.sources,
      })
    }
    return acc
  }, [] as ChatMessage[])
}

const countConversationsFromHistoryItems = (
  items: ChatHistoryResponseItem[]
) => {
  // Count unique conversation pairs (human + robot message pairs)
  // Each item represents one conversation exchange
  return items.length
}

export const formatMessagesFromHistory = (data: ChatHistoryResponse) => {
  // Handle both old format (array) and new format (object with items and pagination)
  if (Array.isArray(data.payload)) {
    // Legacy format: payload is directly an array of items
    return {
      messages: formatMessagesFromHistoryItems(data.payload),
      conversationCount: countConversationsFromHistoryItems(data.payload),
      pagination: undefined,
    }
  }
  // New format: payload has items and pagination
  return {
    messages: formatMessagesFromHistoryItems(data.payload.items),
    conversationCount: countConversationsFromHistoryItems(data.payload.items),
    pagination: data.payload.pagination,
  }
}

const isExplainResponse = (
  response: ChatQueryResponse | ChatExplainResponse
): response is ChatExplainResponse => response.command === 'explain'

export const conversationMessagesUpdater = (
  response: ChatQueryResponse | ChatExplainResponse,
  currentSettings: ChatSettings | undefined
) => {
  const type = response.command === 'query' ? 'robot' : 'explain'

  return (messages: ChatMessage[]) => {
    const index = messages.findIndex((m) => m.type === type && !m.completed)

    if (index === -1) {
      messages.push({
        renderId: uuidv4(),
        type,
        chat_item_id: response.payload?.chat_item_id,
        text: response.payload.message,
        settings: currentSettings ?? {},
        completed: response.payload.status === 'complete',
        references: isExplainResponse(response)
          ? response.payload.references
          : undefined,
        reference_text: isExplainResponse(response)
          ? response.payload.reference_text
          : undefined,
        sub_status: response.payload.sub_status,
        citations: response.payload.citations?.sources,
      })
    } else {
      messages[index] = {
        ...messages[index],
        chat_item_id: response.payload?.chat_item_id,
        text: response.payload.message,
        completed: response.payload.status === 'complete',
        references: isExplainResponse(response)
          ? response.payload.references
          : undefined,
        reference_text: isExplainResponse(response)
          ? response.payload.reference_text
          : undefined,
        settings:
          (response.payload.status === 'complete'
            ? response.payload.settings
            : currentSettings) ?? {},
        sub_status: response.payload.sub_status,
        citations: response.payload.citations?.sources,
      }
    }

    return [...messages]
  }
}
